/**
 * PasswordChangeForm Component
 *
 * Form for changing the user's password with current password verification.
 * Layout: current password full width, then new + confirm in 2-column grid.
 *
 * @module pages/SettingsPage/components/PasswordChangeForm
 */

import {
  type ChangeEvent,
  type FormEvent,
  type ReactElement,
  memo,
  useCallback,
  useState,
} from 'react';
import { Button, Group, PasswordInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import type { ApiError } from '@/types/api';
import { profileService } from '@/services/api';
import { NotificationService } from '@/services/notificationService';

import { PasswordStrengthBar } from './PasswordStrengthBar';
import styles from './PasswordChangeForm.module.css';

const MIN_PASSWORD_LENGTH = 8;

export const PasswordChangeForm = memo(
  function PasswordChangeForm(): ReactElement {
    const { t } = useTranslation();

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

    const handleCurrentPasswordChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        setCurrentPassword(event.currentTarget.value);
      },
      []
    );

    const handleNewPasswordChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        setNewPassword(event.currentTarget.value);
      },
      []
    );

    const handleConfirmPasswordChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        setConfirmPassword(event.currentTarget.value);
      },
      []
    );

    const handleUpdatePassword = useCallback(
      async (event: FormEvent) => {
        event.preventDefault();
        if (!currentPassword || !newPassword || !confirmPassword) return;

        if (newPassword.length < MIN_PASSWORD_LENGTH) {
          NotificationService.showError(
            t('settings.account.password.tooShort')
          );
          return;
        }

        if (newPassword !== confirmPassword) {
          NotificationService.showError(
            t('settings.account.password.mismatch')
          );
          return;
        }

        setIsUpdatingPassword(true);
        try {
          await profileService.updatePassword({
            currentPassword,
            newPassword,
          });
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
          NotificationService.showSuccess(
            t('settings.account.password.success'),
            true
          );
        } catch (error: unknown) {
          const errorCode = (error as ApiError).backendErrorCode;
          if (errorCode === 'ACCOUNT_INVALID_CURRENT_PASSWORD') {
            NotificationService.showError(
              t('settings.account.password.invalidPassword')
            );
            return;
          }
          console.error('Failed to update password:', error);
          NotificationService.showError(t('common.error'));
        } finally {
          setIsUpdatingPassword(false);
        }
      },
      [currentPassword, newPassword, confirmPassword, t]
    );

    const isPasswordFormValid =
      currentPassword.length > 0 &&
      newPassword.length >= MIN_PASSWORD_LENGTH &&
      confirmPassword.length > 0;

    return (
      <form onSubmit={handleUpdatePassword}>
        <PasswordInput
          label={t('settings.account.password.currentPassword')}
          placeholder={t(
            'settings.account.password.currentPasswordPlaceholder'
          )}
          value={currentPassword}
          onChange={handleCurrentPasswordChange}
          autoComplete="current-password"
        />

        <div className={styles.grid}>
          <div>
            <PasswordInput
              label={t('settings.account.password.newPassword')}
              placeholder={t(
                'settings.account.password.newPasswordPlaceholder'
              )}
              value={newPassword}
              onChange={handleNewPasswordChange}
              autoComplete="new-password"
            />
            <PasswordStrengthBar password={newPassword} />
          </div>

          <PasswordInput
            label={t('settings.account.password.confirmPassword')}
            placeholder={t(
              'settings.account.password.confirmPasswordPlaceholder'
            )}
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            autoComplete="new-password"
          />
        </div>

        <Group justify="flex-end" mt="lg">
          <Button
            type="submit"
            loading={isUpdatingPassword}
            disabled={!isPasswordFormValid}
          >
            {t('settings.account.password.save')}
          </Button>
        </Group>
      </form>
    );
  }
);
